import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { FAILED_SKUS_PAGE_SIZE } from '@/constants';
import type { FailedSku, FailedSkusResponse } from '@/types';
import { fetchJobFailedSkus } from '@/utils';
export function useSkuErrorsData(failedSkusJobId: string, isActive: boolean) {
  const [failedSkus, setFailedSkus] = useState<FailedSku[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Refs for tracking state
  const hasScrolledRef = useRef(false);
  const initialLoadCompleteRef = useRef(false);
  const isLoadingRef = useRef(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const modalOpenedRef = useRef(false);

  // Setup intersection observer
  const {
    ref: loadMoreRef,
    inView
  } = useInView({
    threshold: 0,
    delay: 100,
    rootMargin: '200px',
    triggerOnce: false
  });

  // Fetch data when component mounts or when page changes
  useEffect(() => {
    if (!failedSkusJobId || !isActive) {
      return;
    }
    const fetchData = async () => {
      // Guard against concurrent fetches
      if (isLoadingRef.current) {
        return;
      }
      try {
        setLoading(true);
        isLoadingRef.current = true;
        const response = (await fetchJobFailedSkus(failedSkusJobId, {
          pageNumber,
          pageSize: FAILED_SKUS_PAGE_SIZE
        })) as FailedSkusResponse;
        if (!response) {
          return;
        }
        const isFirstPage = pageNumber === 1;

        // Update failed SKUs list
        setFailedSkus(prev => isFirstPage ? [...response.data] : [...prev, ...response.data]);
        setTotalItems(response.total);

        // Update hasMore based on the total items vs current items
        const currentItemCount = isFirstPage ? response.data.length : failedSkus.length + response.data.length;
        setHasMore(currentItemCount < response.total);

        // Handle first page completion
        if (isFirstPage) {
          initialLoadCompleteRef.current = true;
          hasScrolledRef.current = true;
        }
      } catch (error) {
        console.error('Error fetching failed SKUs:', error);
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    };
    fetchData();
  }, [failedSkusJobId, pageNumber, isActive]);

  // Load more when the sentinel comes into view
  useEffect(() => {
    if (inView && hasMore && !isLoadingRef.current && initialLoadCompleteRef.current) {
      setPageNumber(prev_0 => prev_0 + 1);
    }
  }, [inView, hasMore, pageNumber]);

  // Track modal open/close to handle scrolling
  useEffect(() => {
    // Extract the modal handling logic into separate functions
    const handleModalOpen = () => {
      // Reset pagination state
      setPageNumber(1);
      setFailedSkus([]);
      setHasMore(true);
      hasScrolledRef.current = false;
      initialLoadCompleteRef.current = false;
      isLoadingRef.current = false;

      // Reset scroll position when modal opens
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollTop = 0;
        }
      }, 100); // Small timeout to ensure DOM is ready

      modalOpenedRef.current = true;
    };
    const handleModalClose = () => {
      modalOpenedRef.current = false;
    };

    // Determine if the modal is transitioning from closed to open
    const isModalTransitioningToOpen = isActive && !modalOpenedRef.current;

    // Determine if the modal is transitioning from open to closed
    const isModalTransitioningToClose = !isActive && modalOpenedRef.current;

    // Apply the appropriate handler based on the transition
    if (isModalTransitioningToOpen) {
      handleModalOpen();
    } else if (isModalTransitioningToClose) {
      handleModalClose();
    }
  }, [isActive, failedSkusJobId]);
  return {
    failedSkus,
    loading,
    hasMore,
    totalItems,
    tableContainerRef,
    loadMoreRef
  };
}